import Vue from 'vue';
import { mapGetters } from 'vuex';
import CollapsableSettings from "components/widgets/ProjectSettings/CollapsableSettings.vue";
import AutoAddSettings from "components/widgets/ProjectSettings/AutoAddSettings.vue";
import Modal from 'components/widgets/Modal.vue';
import Project from 'src/api/project';
import ToggleSlider from 'components/widgets/ToggleSlider.vue';
import HTTPRetryUtil from 'src/utils/httpretry';
import { BfButton, BfTextInput, BfSpinner } from 'components/Butterfly';
import { SET_PROJECT, UPDATE_PROJECT, CLEAR_REQUEST_ERRORS } from 'src/store/types';
import { uniqueFieldsFromSchema } from 'components/widgets/ProjectSettings/UniqueFields';
import { defineRule, Form, Field } from 'components/forms';
export default Vue.extend({
    components: {
        CollapsableSettings,
        AutoAddSettings,
        BfTextInput,
        BfButton,
        BfSpinner,
        Modal,
        ToggleSlider,
        Form,
        Field,
    },
    data() {
        return {
            isLoadingAutoAdd: true,
            isUpdating: false,
            integrationsDict: {},
            showVerbatimAnnotations: false,
            skipBadDates: false,
            allowStructuredThemes: false,
            weekStartDefault: "Sunday",
            weekStartOptions: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
            queryScopeDefault: 'frame',
            queryScopeOptions: [{
                    value: 'frame',
                    label: 'match on verbatim'
                }, {
                    value: 'sentence',
                    label: 'match on sentence'
                }],
            autoAddFilesEnabled: false,
            // If we change the autoAddFilesEnabled setting from off to on, then
            // we want to immediately run an import
            autoAddFilesEnabledOriginalValue: false,
            autoAddInterval: undefined,
            nextAutoAddTime: undefined,
            lastSucceededTime: undefined,
            lastRunTime: undefined,
            integrationDescription: undefined,
            autoUpdateAnalysesEnabled: false,
            autoAddPartials: false,
            showFileBrowser: false,
            selectedFolder: '',
            diagnostics: {},
            settings: [],
            jobs: [],
            diagnosticsLoading: false,
            diagnosticsGeneralLoading: false,
            diagnosticsJobsLoading: false,
            diagnosticsSettingsLoading: false,
            initialFormValues: {
                projectName: this.$store.getters.currentProject?.name,
            },
            autoAddIntervalOptions: {
                'every hour': {
                    'every': 1,
                    'period': 'hours',
                },
                'every day': {
                    'every': 1,
                    'period': 'days',
                },
                'every 7 days': {
                    'every': 7,
                    'period': 'days',
                },
            },
        };
    },
    computed: {
        ...mapGetters([
            'currentUser', 'currentProject', 'currentSite'
        ]),
        integration() {
            return this.currentProject?.integration;
        },
        integrationBucket() {
            if (this.integration.type === 's3') {
                return this.integrationsDict?.s3?.details?.bucket;
            }
            else if (this.integration.type === 'gcs') {
                return this.integrationsDict?.gcs?.details?.bucket;
            }
            return '';
        },
        uniqueFields() {
            return uniqueFieldsFromSchema(this.currentProject?.schema);
        },
    },
    created() {
        defineRule('project_name_available', async (value) => {
            if (!value)
                return false;
            if (value === this.currentProject?.name)
                return true;
            try {
                await Project.checkProjectName(value);
                return true;
            }
            catch (e) {
                const error = e.body?.non_field_errors?.[0];
                if (error.includes('name already exists')) {
                    this.$store.dispatch(CLEAR_REQUEST_ERRORS);
                    return 'A project with this name already exists';
                }
                if (error.includes('name too long')) {
                    this.$store.dispatch(CLEAR_REQUEST_ERRORS);
                    return 'This project name is too long';
                }
            }
        });
    },
    metaInfo() {
        return {
            title: this.currentProject ? `${this.currentProject?.name} Settings - Kapiche` : null
        };
    },
    beforeMount() {
        this.getAutoAddFilesState();
        this.autoUpdateAnalysesEnabled = this.currentProject?.auto_update_analyses_enabled;
        this.showVerbatimAnnotations = this.currentProject?.show_verbatim_annotations;
        this.skipBadDates = this.currentProject?.skip_bad_dates;
        this.queryScopeDefault = this.currentProject?.query_scope_default;
        this.weekStartDefault = this.currentProject?.week_start;
        this.allowStructuredThemes = this.currentProject?.allow_structured_themes;
    },
    mounted() {
        this.loadIntegrations();
    },
    methods: {
        changeAutoAddInterval(value) {
            this.autoAddInterval = value;
        },
        async updateProject({ projectName }) {
            await this.$store.dispatch({
                type: UPDATE_PROJECT,
                projectId: this.currentProject?.id,
                data: {
                    name: projectName,
                    auto_update_analyses_enabled: this.autoUpdateAnalysesEnabled,
                    show_verbatim_annotations: this.showVerbatimAnnotations,
                    skip_bad_dates: this.skipBadDates,
                    query_scope_default: this.queryScopeDefault,
                    week_start: this.weekStartDefault,
                    allow_structured_themes: this.allowStructuredThemes,
                }
            });
            const data = {
                'enabled': this.autoAddFilesEnabled,
                // If we change the autoAddFilesEnabled setting from off to on then we want to immediately run an import
                'run_immediately': !this.autoAddFilesEnabledOriginalValue && this.autoAddFilesEnabled,
                'selected_folder': '',
                'add_partials': this.autoAddPartials,
                'auto_add_interval': this.autoAddIntervalOptions[this.autoAddInterval],
            };
            if (this.integration) {
                if (this.integration?.type === 's3' || this.integration?.type === 'gcs') {
                    data.selected_folder = this.selectedFolder;
                }
                await Project.updateAutoAddFilesSetting(this.currentProject?.id, data);
            }
            await this.$router.push({ name: 'project-details', params: { projectId: this.currentProject?.id } });
        },
        confirmDeleteProject() {
            this.$refs.projConfirm.show();
        },
        async deleteProject() {
            const projectId = this.currentProject.id;
            await Project.deleteProject(projectId);
            this.$analytics.track.project.delete(projectId);
            this.$store.commit(SET_PROJECT, null);
            await this.$router.push({ name: 'home' });
        },
        loadIntegrations() {
            Project.getAllIntegrations()
                .then((response) => {
                // pack the list into a dict indexed by type
                this.integrationsDict = {};
                for (const i of response.results.filter(r => r.enabled)) {
                    this.integrationsDict[i.type] = i;
                }
            });
        },
        getAutoAddFilesState() {
            this.isLoadingAutoAdd = true;
            Project.getAutoAddFilesSetting(this.currentProject.id)
                .then((response) => {
                this.autoAddFilesEnabled = !!response.enabled;
                this.autoAddInterval = response.interval;
                this.nextAutoAddTime = response.next_run;
                this.lastSucceededTime = response.last_succeeded;
                this.lastRunTime = response.last_ran_at;
                this.integrationDescription = response.description;
                this.autoAddFilesEnabledOriginalValue = response.enabled;
                this.selectedFolder = response.state?.selected_folder;
                this.autoAddPartials = response.state?.add_partial_responses;
                this.isLoadingAutoAdd = false;
            });
        },
        toggleAutoAdd(event) {
            this.autoAddFilesEnabled = event;
            if (event === false && this.integration.type === 'survey_monkey') {
                this.autoAddPartials = false;
            }
        },
        async updateGeneralDiagnostics() {
            this.diagnosticsGeneralLoading = true;
            try {
                let response = await this.getChrysalisDiagnostics(this.currentProject.id, this.currentProject.chrysalis_ref, this.currentSite.chrysalis_url, true, false, false);
                let { ...info } = response;
                this.diagnostics = {
                    ...info,
                    "Chrysalis host": this.currentSite.chrysalis_url
                };
            }
            finally {
                this.diagnosticsGeneralLoading = false;
            }
        },
        async updateSettingsDiagnostics() {
            this.diagnosticsSettingsLoading = true;
            try {
                let response = await this.getChrysalisDiagnostics(this.currentProject.id, this.currentProject.chrysalis_ref, this.currentSite.chrysalis_url, false, true, false);
                this.settings = response.settings;
            }
            finally {
                this.diagnosticsSettingsLoading = false;
            }
        },
        async updateJobsDiagnostics() {
            this.diagnosticsJobsLoading = true;
            try {
                let response = await this.getChrysalisDiagnostics(this.currentProject.id, this.currentProject.chrysalis_ref, this.currentSite.chrysalis_url, false, false, true);
                this.jobs = response.jobs;
            }
            finally {
                this.diagnosticsJobsLoading = false;
            }
        },
        async updateDiagnostics() {
            this.diagnosticsLoading = true;
            try {
                let response = await this.getChrysalisDiagnostics(this.currentProject.id, this.currentProject.chrysalis_ref, this.currentSite.chrysalis_url, true, true, true);
                let { settings, jobs, ...info } = response;
                this.diagnostics = {
                    ...info,
                    "Chrysalis host": this.currentSite.chrysalis_url
                };
                this.settings = settings;
                this.jobs = jobs;
            }
            finally {
                this.diagnosticsLoading = false;
            }
        },
        async getChrysalisDiagnostics(projectId, chrysalisRef, chrysalisUrl, get_general, get_settings_table, get_current_jobs) {
            const urlstr = `projects/${projectId}/tunnel${chrysalisRef}/diagnostics`;
            let response = await HTTPRetryUtil.retry(urlstr, {
                params: {
                    'chrysalis_url': chrysalisUrl,
                    'chrysalisRef': chrysalisRef,
                    // The default for GET is to use the chrysalis cache, but
                    // we don't want that here because live diagnostics are needed.
                    'allow_read_cache': false,
                    'get_general': get_general,
                    'get_settings_table': get_settings_table,
                    'get_current_jobs': get_current_jobs,
                }
            });
            return response;
        },
    }
});
